import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";

const empty = { account_name: "", tool_name: "", login_email: "", login_password: "", max_users: 4, notes: "", tutorial_video_url: "" };

export default function AdminSharedAccounts() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/shared-accounts").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (a) => {
    setEditing(a);
    setForm({
      account_name: a.account_name, tool_name: a.tool_name,
      login_email: a.login_email, login_password: a.login_password,
      max_users: a.max_users, notes: a.notes || "",
      tutorial_video_url: a.tutorial_video_url || "",
    });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, max_users: parseInt(form.max_users) || 1 };
      if (editing) {
        await api.put(`/admin/shared-accounts/${editing.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/admin/shared-accounts", payload);
        toast.success("Created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this shared account?")) return;
    try {
      await api.delete(`/admin/shared-accounts/${id}`);
      toast.success("Deleted");
      load();
    } catch (_) { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">Shared Accounts</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Login credentials shared with paying users.
          </p>
        </div>
        <Button onClick={openNew} data-testid="add-shared"><Plus className="w-4 h-4 mr-2" /> Add Account</Button>
      </div>

      <div className="glass-card rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Account</TableHead>
                <TableHead>Tool</TableHead>
                <TableHead>Login Email</TableHead>
                <TableHead>Capacity</TableHead>
                <TableHead>Status</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((a) => (
                <TableRow key={a.id} data-testid={`sa-${a.id}`}>
                  <TableCell className="font-medium">{a.account_name}</TableCell>
                  <TableCell>{a.tool_name}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{a.login_email || "—"}</TableCell>
                  <TableCell className="font-mono text-sm">{a.current_users} / {a.max_users}</TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-sm ${a.status === "FULL" ? "badge-full" : "badge-paid"}`}>
                      {a.status === "FULL" ? "FULL" : "AVAILABLE"}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(a)} data-testid={`edit-sa-${a.id}`}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant="ghost" className="text-red-500" onClick={() => del(a.id)} data-testid={`del-sa-${a.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="sa-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit Shared Account" : "Add Shared Account"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider">Account Name</Label>
              <Input data-testid="sa-name" value={form.account_name} onChange={(e) => setForm({ ...form, account_name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider">Tool Name (must match Plan's tool name)</Label>
              <Input data-testid="sa-tool" value={form.tool_name} onChange={(e) => setForm({ ...form, tool_name: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider">Login Email</Label>
                <Input data-testid="sa-email" value={form.login_email} onChange={(e) => setForm({ ...form, login_email: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider">Login Password</Label>
                <Input data-testid="sa-password" value={form.login_password} onChange={(e) => setForm({ ...form, login_password: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider">Max Users</Label>
              <Input data-testid="sa-max" type="number" value={form.max_users} onChange={(e) => setForm({ ...form, max_users: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider">Notes (supports **bold** and auto-links URLs)</Label>
              <Textarea data-testid="sa-notes" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Example: **Step 1:** Go to https://example.com/login" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider">Tutorial Video URL (YouTube — optional)</Label>
              <Input data-testid="sa-tutorial" value={form.tutorial_video_url} onChange={(e) => setForm({ ...form, tutorial_video_url: e.target.value })} placeholder="https://www.youtube.com/watch?v=..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="sa-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="sa-save">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
